import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchNotificationsSchedule,
  createNotificationsSchedule,
  updateNotificationsSchedule,
  deleteNotificationsSchedule,
} from "./thunks";
import { toast } from "react-toastify";

import type { ScheduleNotificationTemplate } from "@/types/schedules";

interface NotificationsScheduleState {
  data: ScheduleNotificationTemplate[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsScheduleState = {
  data: null,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: "notificationsSchedule",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationsSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as
          | { data?: ScheduleNotificationTemplate[] }
          | ScheduleNotificationTemplate[]
          | ScheduleNotificationTemplate
          | null;
        if (!payload) {
          state.data = null;
        } else if (Array.isArray(payload)) {
          state.data = payload as ScheduleNotificationTemplate[];
        } else if (
          typeof payload === "object" &&
          "data" in payload &&
          Array.isArray((payload as any).data)
        ) {
          state.data = (payload as any).data as ScheduleNotificationTemplate[];
        } else {
          state.data = [payload as ScheduleNotificationTemplate];
        } // backend might return { data: [...] } or an array or a single item
      })
      .addCase(fetchNotificationsSchedule.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as { error?: string } | null;
        state.error = payload?.error || null;
      })

      .addCase(createNotificationsSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotificationsSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as ScheduleNotificationTemplate | null;
        if (payload) {
          state.data = state.data ? [payload, ...state.data] : [payload];
        }
        toast.success("Template criado com sucesso");
      })
      .addCase(createNotificationsSchedule.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as { error?: string } | null;
        state.error = payload?.error || null;
        toast.error("Não foi possível criar o template");
      })

      .addCase(updateNotificationsSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationsSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload as ScheduleNotificationTemplate;
        if (state.data) {
          state.data = state.data.map((d) =>
            d.id === updated.id ? updated : d,
          );
        }
        toast.success("Template atualizado com sucesso");
      })
      .addCase(updateNotificationsSchedule.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as { error?: string } | null;
        state.error = payload?.error || null;
        toast.error("Não foi possível atualizar o template");
      })

      .addCase(deleteNotificationsSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotificationsSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const id = action.meta.arg as number;
        if (state.data) state.data = state.data.filter((d) => d.id !== id);
        toast.success("Template removido com sucesso");
      })
      .addCase(deleteNotificationsSchedule.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as { error?: string } | null;
        state.error = payload?.error || null;
        toast.error("Não foi possível remover o template");
      });
  },
});

export default slice.reducer;
